import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { CheckCircle, XCircle, Eye, AlertCircle, Clock } from 'lucide-react';
import api from '../../../../config/api';
import HospitalDetails from './HospitalDetails';

const AdminHospitals = () => {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchHospitals();
  }, []);

  const fetchHospitals = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/hospitals');
      setHospitals(response.data);
    } catch (error) {
      console.error('Error fetching hospitals:', error);
      toast.error('Failed to load hospitals');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (hospitalId, newStatus) => {
    try {
      await api.put(`/admin/hospitals/${hospitalId}/status`, { status: newStatus });
      toast.success(`Hospital ${newStatus === 'approved' ? 'approved' : 'rejected'} successfully`);
      
      // Update local state
      setHospitals(hospitals.map(hospital => 
        hospital._id === hospitalId ? { ...hospital, status: newStatus } : hospital
      ));
    } catch (error) {
      console.error('Error updating hospital status:', error);
      toast.error('Failed to update hospital status');
    }
  };

  const viewHospitalDetails = (hospital) => {
    setSelectedHospital(hospital);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedHospital(null);
  };

  const filteredHospitals = filterStatus === 'all' 
    ? hospitals 
    : hospitals.filter(hospital => hospital.status === filterStatus);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 flex items-center">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 flex items-center">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 flex items-center">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Hospital Management</h1>
        <div className="flex space-x-2">
          <select 
            className="border rounded-md px-3 py-1.5 text-sm bg-white"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Hospitals</option>
            <option value="pending">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <button 
            onClick={fetchHospitals}
            className="bg-blue-50 text-blue-600 px-3 py-1.5 rounded-md text-sm hover:bg-blue-100"
          >
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredHospitals.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No hospitals found</h3>
          <p className="mt-2 text-sm text-gray-500">
            {filterStatus === 'all' 
              ? 'There are no hospitals registered in the system.' 
              : `There are no hospitals with ${filterStatus} status.`}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hospital Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredHospitals.map((hospital) => (
                <tr key={hospital._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{hospital.name}</div>
                    {hospital.registrationNumber && (
                      <div className="text-xs text-gray-500">Reg: {hospital.registrationNumber}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{hospital.email}</div>
                    <div className="text-xs text-gray-500">{hospital.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{hospital.address?.city}</div>
                    <div className="text-xs text-gray-500">{hospital.address?.state}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(hospital.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => viewHospitalDetails(hospital)}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </button>
                      {hospital.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleStatusChange(hospital._id, 'approved')}
                            className="text-green-600 hover:text-green-900 flex items-center"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleStatusChange(hospital._id, 'rejected')}
                            className="text-red-600 hover:text-red-900 flex items-center"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </button>
                        </>
                      )}
                      {hospital.status !== 'pending' && (
                        <button
                          onClick={() => handleStatusChange(hospital._id, 'pending')}
                          className="text-yellow-600 hover:text-yellow-900 flex items-center"
                        >
                          <Clock className="w-4 h-4 mr-1" />
                          Reset
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showDetailsModal && selectedHospital && (
        <HospitalDetails 
          hospital={selectedHospital} 
          onClose={closeDetailsModal} 
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
};

export default AdminHospitals;